import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { PageHeader } from "@/components/admin/PageHeader";
import { RequirePermission } from "@/components/admin/RequirePermission";
import { notify } from "@/lib/admin/notify";
import {
  getAiChatSettingsFn,
  saveAiChatSettingsFn,
  testAiChatFn,
} from "@/lib/ai-chat/ai-chat.functions";
import {
  DEFAULT_SYSTEM_INSTRUCTIONS,
  type AiChatSettingsPublicAdmin,
} from "@/lib/ai-chat/types";

export const Route = createFileRoute("/admin/ai-chat")({
  head: () => ({
    meta: [
      { title: "AI Chat Settings — CB16 Console" },
      {
        name: "description",
        content: "Configure the CB16 on-site AI fragrance assistant, persona and fallback message.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <RequirePermission perm="settings.read">
      <AiChatSettingsPage />
    </RequirePermission>
  ),
});

const card = "rounded-sm border border-black/10 bg-white p-5";
const label = "text-[10px] font-semibold uppercase tracking-[0.2em] text-black/60";
const input =
  "w-full border border-black/15 bg-white px-3 py-2 text-sm text-black focus:border-[#C6A15B] focus:outline-none";
const btn =
  "inline-flex items-center justify-center border border-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B] disabled:cursor-wait disabled:opacity-50";
const btnSolid =
  "inline-flex items-center justify-center border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition hover:bg-[#C6A15B] hover:border-[#C6A15B] disabled:cursor-wait disabled:opacity-50";

function AiChatSettingsPage() {
  const load = useServerFn(getAiChatSettingsFn);
  const save = useServerFn(saveAiChatSettingsFn);
  const test = useServerFn(testAiChatFn);

  const [settings, setSettings] = useState<AiChatSettingsPublicAdmin | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void load().then(setSettings).catch((err: unknown) => {
      notify.error("Could not load AI chat settings", err instanceof Error ? err.message : "Try again.");
    });
  }, [load]);

  const patch = (p: Partial<AiChatSettingsPublicAdmin>) =>
    setSettings((s) => (s ? { ...s, ...p } : s));

  const commit = async (clearApiKey = false) => {
    if (!settings) return;
    setSaving(true);
    try {
      const next = await save({
        data: {
          enabled: settings.enabled,
          provider: settings.provider,
          modelId: settings.modelId,
          systemInstructions: settings.systemInstructions,
          welcomeMessage: settings.welcomeMessage,
          fallbackMessage: settings.fallbackMessage,
          temperature: settings.temperature,
          maxTokens: settings.maxTokens,
          ...(clearApiKey ? { clearApiKey: true } : apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        },
      });
      setSettings(next);
      setApiKey("");
      notify.success("AI chat settings saved", "The storefront assistant uses these settings immediately.");
    } catch (err) {
      notify.error("Could not save settings", err instanceof Error ? err.message : "Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="AI Chat Settings" subtitle="On-site fragrance assistant configuration" />
        <div className={card}>
          <p className="text-sm text-black/50">Loading configuration…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="AI Chat Settings"
        subtitle="On-site fragrance assistant — provider, persona and fallback"
        action={
          <button className={btnSolid} disabled={saving} onClick={() => void commit()}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        }
      />

      {/* Status */}
      <section className={card}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-black">Status</h2>
            <p className="mt-1 text-sm text-black/60">
              When disabled, the storefront chat replies with your fallback message.
            </p>
          </div>
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
            />
            <span
              className={
                "rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] " +
                (settings.enabled ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")
              }
            >
              {settings.enabled ? "Live" : "Offline"}
            </span>
          </label>
        </div>
      </section>

      {/* Provider */}
      <section className={card}>
        <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-black">Provider</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="grid gap-1">
            <span className={label}>Provider</span>
            <select
              className={input}
              value={settings.provider}
              onChange={(e) =>
                patch({
                  provider: e.target.value as AiChatSettingsPublicAdmin["provider"],
                  modelId:
                    e.target.value === "openrouter" ? "openrouter/auto" : "google/gemini-3.7-flash",
                })
              }
            >
              <option value="openrouter">OpenRouter (your API key)</option>
              <option value="lovable">CB16 managed AI (no key required)</option>
            </select>
          </label>
          <label className="grid gap-1">
            <span className={label}>Model ID</span>
            <input
              className={input}
              value={settings.modelId}
              onChange={(e) => patch({ modelId: e.target.value })}
              placeholder="e.g. openai/gpt-4o-mini"
            />
          </label>
          {settings.provider === "openrouter" && (
            <label className="grid gap-1 md:col-span-2">
              <span className={label}>
                OpenRouter API key {settings.hasApiKey ? "(saved — leave blank to keep)" : "(required)"}
              </span>
              <div className="flex flex-wrap gap-2">
                <input
                  className={input + " flex-1"}
                  type="password"
                  autoComplete="off"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={settings.hasApiKey ? "•••••••••••••••• stored" : "sk-or-…"}
                />
                {settings.hasApiKey && (
                  <button className={btn} disabled={saving} onClick={() => void commit(true)}>
                    Remove key
                  </button>
                )}
              </div>
              <span className="text-[11px] text-black/45">
                Stored securely on the server. It is never sent to the browser or the storefront.
              </span>
            </label>
          )}
          <label className="grid gap-1">
            <span className={label}>Creativity (0–2)</span>
            <input
              className={input}
              type="number"
              step="0.1"
              min={0}
              max={2}
              value={settings.temperature}
              onChange={(e) => patch({ temperature: Number(e.target.value) || 0 })}
            />
          </label>
          <label className="grid gap-1">
            <span className={label}>Max reply length (tokens)</span>
            <input
              className={input}
              type="number"
              min={64}
              max={4000}
              value={settings.maxTokens}
              onChange={(e) => patch({ maxTokens: Number(e.target.value) || 500 })}
            />
          </label>
        </div>
      </section>

      {/* Persona */}
      <section className={card}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-black">
            CB16 assistant instructions
          </h2>
          <button
            className={btn}
            onClick={() => patch({ systemInstructions: DEFAULT_SYSTEM_INSTRUCTIONS })}
          >
            Reset to CB16 default
          </button>
        </div>
        <p className="mt-1 text-sm text-black/60">
          The assistant automatically receives your published FAQ page and published fragrance catalog
          as its knowledge source.
        </p>
        <textarea
          className={input + " mt-4 min-h-[240px] font-mono text-[12px] leading-relaxed"}
          value={settings.systemInstructions}
          onChange={(e) => patch({ systemInstructions: e.target.value })}
        />
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="grid gap-1">
            <span className={label}>Welcome message</span>
            <input
              className={input}
              value={settings.welcomeMessage}
              onChange={(e) => patch({ welcomeMessage: e.target.value })}
            />
          </label>
          <label className="grid gap-1">
            <span className={label}>Fallback message (assistant offline)</span>
            <input
              className={input}
              value={settings.fallbackMessage}
              onChange={(e) => patch({ fallbackMessage: e.target.value })}
            />
          </label>
        </div>
      </section>

      <RequirePermission perm="settings.read" fallback={null}>
        <TestChat runTest={test} />
      </RequirePermission>
    </div>
  );
}

function TestChat({ runTest }: { runTest: (args: { data: unknown }) => Promise<{ reply: string; ok: boolean; error: string | null }> }) {
  const [messages, setMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [draft, setDraft] = useState("Which CB16 fragrance would you recommend as a gift?");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user" as const, content: text }];
    setMessages(next);
    setDraft("");
    setBusy(true);
    setError(null);
    try {
      const res = await runTest({ data: { messages: next } });
      setMessages([...next, { role: "assistant", content: res.reply }]);
      if (!res.ok) setError(res.error ?? "The assistant is not responding.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={card}>
      <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-black">Test chat</h2>
      <p className="mt-1 text-sm text-black/60">
        Save your changes first, then verify the live configuration here.
      </p>
      {error && (
        <p className="mt-3 border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</p>
      )}
      <div
        ref={boxRef}
        className="mt-4 max-h-72 space-y-3 overflow-y-auto border border-black/10 bg-[#F7F5F1] p-4"
      >
        {messages.length === 0 && (
          <p className="text-sm text-black/40">No test messages yet.</p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div
              className={
                "max-w-[80%] rounded-sm px-3 py-2 text-sm " +
                (m.role === "user" ? "bg-[#C6A15B] text-white" : "border border-black/10 bg-white text-black")
              }
            >
              {m.content}
            </div>
          </div>
        ))}
        {busy && <p className="text-[11px] uppercase tracking-[0.2em] text-black/40">Thinking…</p>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <input
          className={input + " flex-1"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void send();
          }}
          placeholder="Ask the CB16 assistant…"
        />
        <button className={btnSolid} disabled={busy} onClick={() => void send()}>
          {busy ? "Sending…" : "Send"}
        </button>
        <button className={btn} onClick={() => { setMessages([]); setError(null); }}>
          Clear
        </button>
      </div>
    </section>
  );
}
